import React from "react";
import { getRequest } from "sharedUtils/httpUtils";
import MUIDataTable from "mui-datatables";
import TextField from '@material-ui/core/TextField';
import { FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import { convertToNumericMonthAndDayAndYearAndTime } from "sharedUtils/timeUtils";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import AccountBanner from "../../components/AccountBanner"
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

const { DateTime } = require("luxon");

class CreatorClicksByDateAndHour extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();

        this.state = {
            startDate: this.getDateFromPropsOrDefault("startDate").startOf("day"),
            endDate: this.getDateFromPropsOrDefault("endDate").endOf("day"),
            startHour: 0,
            endHour: 23,
            minDate: "1753-01-01",
            maxDate: "9999-12-31",
            dateError: false,
            isLoading: false,
            clicks: [],
            selected: [],
            userSelected: this.props.location?.state?.["userName"]
                ? [{ userName: this.props.location.state["userName"] }]
                : [],
            options: [],
            userOptions: [],
            userValue: "",
            clicksLoading: false,
            CreatorClicksTable: {
                title: "Creator Clicks",
                options: {                    
                    rowsPerPage: 100,
                    sortOrder: {
                        name: 'date',
                        direction: 'asc'
                    },
                },
                getTheme: () => createTheme({
                    typography: {
                        fontFamily: "'Urbanist', 'sf_pro_displayregular', sans-serif",
                    },
                    overrides: {
                        MUIDataTableBodyRow: {
                            root: {
                                '&:nth-child(odd)': {
                                    backgroundColor: '#F9F9F9'
                                },
                                cursor: 'pointer'
                            }
                        },
                    }
                })
            },
        };
    }

    componentDidMount = () => {
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });

        if (this.props.location?.state?.userName) {
            this.setUserFromPropsOrFetch(this.props.location.state.userName);
        }

        this.getClicks();
    };

    componentDidUpdate = async (prevProps, prevState) => {
        const { startDate, endDate, minDate, maxDate, startHour, endHour, userSelected } = this.state;

        // Only run validation if dates changed
        if (
            prevState.startDate !== startDate ||
            prevState.endDate !== endDate ||
            prevState.startHour !== startHour ||
            prevState.endHour !== endHour ||
            prevState.userSelected !== userSelected
        ) {
            // Convert minDate/maxDate (which are strings) to Luxon DateTime
            const minDateObj = DateTime.fromISO(minDate);  // "1753-01-01"
            const maxDateObj = DateTime.fromISO(maxDate);  // "9999-12-31"

            // Check:
            // 1) Both dates are valid
            // 2) Both dates fall within [minDate, maxDate]
            // 3) startDate < endDate
            if (
                startDate?.isValid && endDate?.isValid &&
                startDate >= minDateObj && startDate <= maxDateObj &&
                endDate >= minDateObj && endDate <= maxDateObj &&
                startDate <= endDate
            ) {
                this.setState({ dateError: false });
                await this.getClicks();
            } else {
                this.setState({ dateError: true });
            }
        }
    };

    getDateFromPropsOrDefault = (key) => {
        const dateFromProps = this.props[key]
            ? DateTime.fromISO(this.props[key]) // Handles direct Luxon objects or ISO strings
            : this.props.location?.state?.[key]
                ? DateTime.fromISO(this.props.location.state[key]) // Handles passed state from history.push
                : null;

        return dateFromProps?.isValid ? dateFromProps : DateTime.utc(); // Fallback to UTC if invalid
    };

    setUserFromPropsOrFetch = async (userName) => {
        try {
            var users = await this.fetchUsers(userName);
            if (!!(users) && users.length > 0 && !!(users[0])) {
                const user = users[0]

                const userArray = [...user]
                await this.setUserSelected(userArray);
                this.getClicks();
            }
        } catch (error) {
            console.error("Error fetching user:", error);
        }
        // Clear state after first read
        if (this.props.history) {
            this.props.history.replace({
                ...this.props.location,
                state: {}
            });
        }
    };

    handleStartDateChange = (event) => {
        const startDateRaw = event.target.value;
        if (startDateRaw.length === 0) return;

        const startDate = DateTime.fromISO(startDateRaw).toUTC().startOf('day');
        if (startDate.isValid) {
            if (this.state.hourly){
                this.setState({ startDate, endDate: startDate });
            } else {
                this.setState({ startDate });
            }
        } else {
            console.error("Invalid start date entered:", startDateRaw);
        }
    };

    handleEndDateChange = (event) => {
        const endDateRaw = event.target.value;
        if (endDateRaw.length === 0) return;

        const endDate = DateTime.fromISO(endDateRaw).toUTC().endOf('day');
        if (endDate.isValid) {
            this.setState({ endDate });
        } else {
            console.error("Invalid end date entered:", endDateRaw);
        }
    };

    handleWeekToDate = () => {
        const startDate = DateTime.utc().minus({ weeks: 1 }).startOf('day');
        this.setState({ startDate, endDate: DateTime.utc().endOf('day') });
    };

    handleMonthToDate = () => {
        const startDate = DateTime.utc().minus({ months: 1 }).startOf('day');
        this.setState({ startDate, endDate: DateTime.utc().endOf('day') });
    };

    handleYearToDate = () => {
        const startDate = DateTime.utc().minus({ years: 1 }).startOf('day');
        this.setState({ startDate, endDate: DateTime.utc().endOf('day') });
    };

    handleHourlyChange = (event) => {
        const hourly = event?.target?.checked ?? false; // Safely get the checked value or default to false

        this.setState({ hourly, endDate: this.state.startDate, startHour: 0, endHour: 23 }, () => this.getClicks());
    };

    breadCrumbList = [{ link: '/Admin', text: 'Admin' }, { link: '/Admin/CreatorClicks', text: 'Creator Clicks' }];

    
    getClicks = async () => {
        let url = `api/Merchant/GetCreatorClicksByDateAndHour`;
        const { startDate, endDate, startHour, endHour, userSelected } = this.state;

        let now = DateTime.now();
        let offset = now.offset;

        let adjustedStartDate = startDate.set({ hour: startHour ?? 0, minute: 0, second: 0, millisecond: 0 });
        let adjustedEndDate = endDate.set({ hour: endHour ?? 23, minute: 59, second: 59, millisecond: 999 });

        if (adjustedStartDate > adjustedEndDate) {
            this.setState({ dateError: true });
            return;
        }

        if (adjustedStartDate) {
            url += `?startDate=${adjustedStartDate.toSQL({ includeOffset: false })}`;
        }

        if (adjustedEndDate) {
            url += url.includes('?')
                ? `&endDate=${adjustedEndDate.toSQL({ includeOffset: false })}`
                : `?endDate=${adjustedEndDate.toSQL({ includeOffset: false })}`;
        }

        url += `&offset=${offset}`;

        if (userSelected?.[0]?.userName?.length > 0) {
            url += `&userName=${userSelected[0].userName}`;
        }

        this.setState({ clicksLoading: true, clicks: [] });

        try {
            const clicks = await getRequest(url);

            const clicksWithLinks = this.getRockporchLinks(clicks);

            const subaffiliateKeys = ['tag', 'sid', 'ctc', 'clickRef', 'subId1', 'pubref', 'u1', 'afftrack'];

            const clicksWithSubaffiliateId = clicksWithLinks.map(click => {
                const params = new URLSearchParams((click.referralLink || '').split('?')[1] || '');
                const subaffiliateId = subaffiliateKeys.map(k => params.get(k)).find(v => v);
                return { ...click, subaffiliateId };
            });

            this.setState({ clicks: clicksWithSubaffiliateId, clicksLoading: false });
        } catch (error) {
            console.error("Error fetching clicks:", error);
            this.setState({ clicks: [], clicksLoading: false });
        }
    };

    setUserSelected = (userSelected) => {
        if (userSelected != null) {
            this.setState( userSelected );
        }
    };

    getRockporchLinks = clicks => {
        return clicks.map(click => {
            let rockporchLink = "";
            let baseUrl = `${window.location.origin}`;

            if (click.shareSubdomain) {
                const url = new URL(baseUrl);

                if (url.host.startsWith("www.")) {
                    // Replace 'www.' with the shareSubdomain
                    url.host = url.host.replace("www.", `${click.shareSubdomain}.`);
                }

                baseUrl = url.toString().replace(/\/$/, ""); // Remove trailing slash
            }


            if (click.type === "UserMerchantProductClick") {
                rockporchLink = `${baseUrl}/prdlink/${click.shareCode}`;
            } else if (click.type === "MerchantClick") {
                rockporchLink = `${baseUrl}/mlink/${click.merchantId}?userId=${click.userId}`;
                if (click.referralLink && click.referralLink.length > 0) {
                    rockporchLink += `&redirectUrl=${encodeURIComponent(click.referralLink)}`;
                }
            }

            return { ...click, rockporchLink };
        });
    }

    handleStartHourChange = (event) => {
        this.setState({ startHour: event.target.value });
    };

    handleEndHourChange = (event) => {
        this.setState({ endHour: event.target.value });
    };

    fetchUsers = async (query) => {
        this.setState({ isLoading: true });
        let url = `/api/Account/GetUserSuggestionsByUsername?username=${query}`;

        let users = await getRequest(url);
        this.setState({ userOptions: users, isLoading: false });
        return users;
    }

    render() {
        const columns = [
           {
                name: "date",
                label: "Date",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        if (!value) {
                            return "-";
                        }
                        try {
                            return convertToNumericMonthAndDayAndYearAndTime(value, false).replace(',', '');
                        } catch (error) {
                            console.error("Invalid date encountered:", value, error);
                            return "-";
                        }
                    },
                },
            },
             {
                name: "type",
                label: "Click Type",
                options: {
                    filter: true,
                    display: false,
                },
            },
            {
                name: "rockporchLink",
                label: "RockPorch Link",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <a href={value} style={{ width: "300px" }} target="_blank" rel="noreferrer">{value}</a>
                    },
                },
            },
            {
                name: "userName",
                label: "Creator",
                options: {
                    filter: true,
                    sort: true,
                },
            },
                        {
                name: "userId",
                label: "UserId",
                options: {
                    filter: true,
                    sort: true,
                    display: false,
                },
            },
            {
                name: "merchantId",
                label: "Merchant ID",
                options: {
                    filter: true,
                    display: false,
                },
            },
            {
                name: "merchantName",
                label: "Brand",
                options: {
                    filter: true,
                    display: true,
                },
            },
            {
                name: "shareCode",
                label: "ShareCode",
                options: {
                    filter: true,
                    display: false,
                },
            },
            {
                name: "referralLink",
                label: "Referral Link",
                options: {
                    filter: true,
                    sort: true,
                    display: false,
                    customBodyRender: (value) => {
                        return <div style={{ maxWidth: "300px", wordWrap: "break-word" }}>{value}</div>
                    },
                }
            },
            {
                name: "referralUrl",
                label: "Referral URL",
                options: {
                    filter: true,
                    sort: true,
                    display: false,
                    customBodyRender: (value) => {
                        return <div style={{ maxWidth: "300px", wordWrap: "break-word" }}>{value}</div>
                    },
                }
            },
            {
                name: "referrer",
                label: "Referrer",
                options: {
                    filter: true,
                    sort: true,
                    display: false,
                },
                customBodyRender: (value) => {
                    return <div style={{ maxWidth: "300px", wordWrap: "break-word" }}>{value}</div>
                },
            },
            {
                name: "subaffiliateId",
                label: "Subaffiliate ID",
                options: {
                    filter: true,
                    sort: true,
                    display: false,
                    customBodyRender: (value) => {
                        if (value == undefined || value == null) return "";
                        return <>{value}</>
                    },
                },
            },
        ];

        return (
            <>
                <section>
                    <AccountBanner breadCrumbList={this.breadCrumbList}>
                        <h1 className="mb-4">Creator Clicks</h1>
                        <p className="mb-0">Look up Product and Merchant clicks by date or hour</p>
                    </AccountBanner>
                </section>

                <div>
 

                    <section className="gray-bg mb-0 pt-0">

                        <div className="d-flex justify-content-between py-3">
                            <div className="pl-5" style={{ minWidth: "285px" }}>
                                {
                                    this.props.location?.state?.userName?.length > 0 ?
                                        <></>
                                        :
                                        <AsyncTypeahead
                                            id="userSearch"
                                            labelKey="userName"
                                            selected={this.state.userSelected} // should be an array
                                            clearButton
                                            onSearch={this.fetchUsers}
                                            onChange={selected => this.setState({ userSelected: selected })}
                                            isLoading={this.state.isLoading}
                                            options={this.state.userOptions}
                                            placeholder="OPTIONAL userName search..."
                                        />

                                }                               

                            </div>  
                            <div className="d-flex w-100 justify-content-end pr-2" style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                <div onClick={this.handleWeekToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', border: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '4px 0 0 4px' }}>1w</div>
                                <div onClick={this.handleMonthToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', borderTop: '1px solid lightgray', borderBottom: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>1m</div>
                                <div onClick={this.handleYearToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', border: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '0 4px 4px 0' }}>1y</div>
                            </div>
                        </div>

                        <p className="ml-5 pt-3 mb-0" style={{ color: "red", display: this.state.dateError ? "" : "none" }}>Dates must be valid dates and Start Date must be before the End Date </p>

                        <div className="pt-0 pb-0 px-5">

                            <div className={`mx-0 d-flex justify-content-between pb-4 ${["sm"].includes(this.props.deviceSize) ? "ml-2 mr-2" : "pt-0"} mx-auto`}>

                                <div className={`d-flex ${["sm"].includes(this.props.deviceSize) ? "w-100 pt-3 mx-2" : ""} justify-content-${["sm"].includes(this.props.deviceSize) ? "between" : "start"}  ${["sm"].includes(this.props.deviceSize) ? "flex-column" : ""} `} style={{ gap: ["sm"].includes(this.props.deviceSize) ? "20px" : "60px", fontFamily: "'Urbanist',sans-serif" }}>

                                    <div className={`d-flex`} style={{ gap: "15px" }}>
                                        <div className="d-flex flex-column">
                                            <label className="mb-0">Start Date:</label>
                                            <TextField
                                                id="start-date"
                                                type="date"
                                                value={this.state.startDate.toISODate()}
                                                onChange={this.handleStartDateChange}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                InputProps={{
                                                    style: { fontFamily: "'Urbanist', sans-serif" },
                                                }} />
                                        </div>
                                        <div className="d-flex flex-column" style={{ width: "95px", position: "relative", top: "7px" }}>
                                            <FormControl sx={{ width: "95px" }}>
                                                <InputLabel id="start-hour-label">Time</InputLabel>
                                                <Select
                                                    labelId="start-hour-label"
                                                    id="start-hour"
                                                    value={this.state.startHour ?? ""}
                                                    onChange={this.handleStartHourChange}
                                                    sx={{
                                                        fontFamily: "'Urbanist', sans-serif",
                                                        height: "40px", // Controls height of select field
                                                    }}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                maxHeight: 300, // Controls dropdown menu height
                                                                width: "95px", // Controls dropdown width
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {Array.from({ length: 24 }, (_, i) => {
                                                        const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
                                                        const ampm = i < 12 ? "AM" : "PM";
                                                        return (
                                                            <MenuItem key={i} value={i}>
                                                                {hour12}:00 {ampm}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>


                                        </div>
                                    </div>
                                    <div className="d-flex" style={{ gap: "15px" }}>

                                        <div className="d-flex flex-column">
                                            <label className="mb-0">End Date:</label>
                                            <TextField
                                                id="end-date"
                                                type="date"
                                                value={this.state.endDate.toISODate()}
                                                onChange={this.handleEndDateChange}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                InputProps={{
                                                    style: { fontFamily: "'Urbanist', sans-serif" },
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex flex-column" style={{ width: "95px", position: "relative", top: "7px" }}>
                                            <FormControl sx={{ width: "95px" }}>
                                                <InputLabel id="start-hour-label">Time</InputLabel>
                                                <Select
                                                    labelId="start-hour-label"
                                                    id="start-hour"
                                                    value={this.state.endHour ?? ""}
                                                    onChange={this.handleEndHourChange}
                                                    sx={{
                                                        fontFamily: "'Urbanist', sans-serif",
                                                        height: "40px", // Controls height of select field
                                                    }}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                maxHeight: 300, // Controls dropdown menu height
                                                                width: "95px", // Controls dropdown width
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {Array.from({ length: 24 }, (_, i) => {
                                                        const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
                                                        const ampm = i < 12 ? "AM" : "PM";
                                                        return (
                                                            <MenuItem key={i} value={i}>
                                                                {hour12}:59 {ampm}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    
                                </div>
                                
                            </div>
                        </div>

                    </section>

                    <section>
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col">
                                    <ThemeProvider theme={this.state.CreatorClicksTable.getTheme()}>
                                        <MUIDataTable
                                            title={
                                                <div style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                                    <p style={{ fontSize: '22px', marginTop: "20px" }}>Creator Clicks</p>
                                                    <p style={{ fontSize: '14px', marginBottom: "0" }}>Lookup Product and Merchant Link clicks by date and time</p>
                                                </div>
                                            }
                                            data={this.state.clicks}
                                            columns={columns}
                                            options={{
                                                ...this.state.CreatorClicksTable.options,
                                                textLabels: {
                                                    body: { noMatch: this.state.clicksLoading ? "LOADING..." : "No data available" }
                                                }
                                            }}
                                            ref={this.tableRef}
                                        />
                                    </ThemeProvider>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </>
        );
    }
}

export default CreatorClicksByDateAndHour;
